<template>
  <PageHeader
    breadcrumb
    :items="[
      { label: $t('ui.entities.report.label', 2), to: '/reports' },
      { label: 'Appointments' },
      { label: 'Source Conversions' }
    ]"
  >
    <template #right>
      <div class="flex gap-2 items-stretch lg:items-center flex-col lg:flex-row flex-wrap md:gap-4 w-full lg:w-auto">
        <DealershipSelect
          v-model="dealershipsSelected"
          :chips="false"
          mode="multiple"
          placeholder="Select a Dealership"
        />
        <div class="flex items-center gap-2 flex-wrap w-full lg:w-auto">
          <DatePicker
            v-model="dateRange"
            mode="range"
            :max-range="93"
            :presets="dateRangePresets"
          />
          <Button
            label="Update"
            :is-loading="conversionsApiRequest.isLoading.value"
            :disabled="!dealershipsSelected.length"
            @click="onUpdateData"
          />
        </div>
      </div>
    </template>
  </PageHeader>
  <Card>
    <DataTable
      ref="dt"
      v-model:filters="filters"
      export-filename="Source Conversions"
      :value="conversionsData"
      removable-sort
      sort-field="timestamp"
      :sort-order="-1"
      data-key="id"
      column-resize-mode="fit"
      :paginator="true"
      responsive-layout="scroll"
      filter-display="menu"
      :global-filter-fields="['dealershipName']"
      :rows="20"
      :rows-per-page-options="[10, 20, 50]"
      state-key="dt-report-source-conversions"
      state-storage="session"
      current-page-report-template="Showing {startRecord}-{endRecord} out of {totalRecords}"
      :loading="conversionsApiRequest.isLoading.value"
    >
      <template #empty>
        <Empty
          v-if="!conversionsApiRequest.isLoading.value && !dealershipsSelected.length"
          title="Select a dealership"
          icon="car-building"
        />
        <div v-else-if="!conversionsApiRequest.isLoading.value">No records found</div>
      </template>

      <template #header>
        <div class="flex justify-between">
          <span class="flex justify-between gap-4">
            <span>
              <Input
                v-model="filters['global'].value"
                :placeholder="$t('ui.common.search', 1)"
                icon="search"
              />
            </span>
            <Button
              :label="$t('ui.actions.clear', 1)"
              severity="secondary"
              type="button"
              outlined
              icon="filter-slash"
              @click="clearFilters()"
            />
          </span>
        </div>
        <div class="flex justify-between mt-4">
          <span>
            <Button
              label="CSV"
              type="button"
              severity="secondary"
              outlined
              icon="file-excel"
              @click="exportCSV($event)"
            />
          </span>
        </div>
      </template>

      <ColumnGroup type="header">
        <Row>
          <Column />
          <Column
            header="Booked"
            :colspan="3"
          />
          <Column
            header="Dealt"
            :colspan="3"
          />
        </Row>

        <Row>
          <Column
            header="Dealership"
            sortable
            field="dealershipId"
          />
          <Column
            field="totals.callCenter"
            sortable
          >
            <template #header>Call Center</template>
          </Column>
          <Column
            field="totals.dealer"
            sortable
          >
            <template #header>Dealer</template>
          </Column>
          <Column
            field="totals.customer"
            sortable
          >
            <template #header>Customer</template>
          </Column>

          <Column
            field="totalsDealt.callCenter"
            sortable
          >
            <template #header>Call Center</template>
          </Column>
          <Column
            field="totalsDealt.dealer"
            sortable
          >
            <template #header>Dealer</template>
          </Column>
          <Column
            field="totalsDealt.customer"
            sortable
          >
            <template #header>Customer</template>
          </Column>
        </Row>
      </ColumnGroup>

      <Column
        field="dealershipName"
        header="Dealership Name"
        hidden
      />

      <Column
        field="dealershipId"
        header="Dealership"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.dealershipName }}
        </template>
      </Column>

      <Column
        field="totals.callCenter"
        header="Call Center"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.totals.callCenter }}
        </template>
      </Column>
      <Column
        field="totals.dealer"
        header="Dealer"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.totals.dealer }}
        </template>
      </Column>
      <Column
        field="totals.customer"
        header="Customer"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.totals.customer }}
        </template>
      </Column>

      <Column
        field="totalsDealt.callCenter"
        header="Call Center"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.totalsDealt.callCenter }}
        </template>
      </Column>
      <Column
        field="totalsDealt.dealer"
        header="Dealer"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.totalsDealt.dealer }}
        </template>
      </Column>
      <Column
        field="totalsDealt.customer"
        header="Customer"
        :sortable="true"
      >
        <template #body="{ data }">
          {{ data.totalsDealt.customer }}
        </template>
      </Column>
    </DataTable>
  </Card>
</template>

<script setup>
import { ref, watch } from 'vue'

import Card from '@/components/card/Card.vue'
import PageHeader from '@/components/page/PageHeader.vue'
import DatePicker from '@/components/date-picker/DatePicker.vue'
import Input from '@/components/forms/Input.vue'
import Button from '@/components/button/Button.vue'
import DealershipSelect from '@/components/forms/DealershipSelect.vue'
import Empty from '@/components/empty/Empty.vue'

import useApiRequest from '@/composables/useApiRequest'
// stores
import { useAuthStore } from '@/stores/AuthStore.ts'
// utilities
import { alertToast } from '@/utilities/notification'
import { datePresets } from '@/utilities/date-time'

import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import ColumnGroup from 'primevue/columngroup'
import Row from 'primevue/row'
import { FilterMatchMode } from '@primevue/core/api'
import dayjs from 'dayjs'

const dt = ref()
const authStore = useAuthStore()
const dealershipsAllowed = ref(authStore.accessAllowed.dealerships)
const dealershipsSelectedStorageKey = 'report-appointment-source-conversions-dealerships-selected'
const dealershipsSelected = ref([])

try {
  const stored = localStorage.getItem(dealershipsSelectedStorageKey)
  if (stored) {
    dealershipsSelected.value = JSON.parse(stored)
  }
} catch {
  dealershipsSelected.value = []
}

// Drop IDs the user no longer has access to
dealershipsSelected.value = dealershipsSelected.value.filter(id => dealershipsAllowed.value.some(d => d.id === id))

// A single allowed site can be pre-selected so the report loads immediately
if (dealershipsAllowed.value.length === 1 && dealershipsSelected.value.length === 0) {
  dealershipsSelected.value = [dealershipsAllowed.value[0].id]
}

localStorage.setItem(dealershipsSelectedStorageKey, JSON.stringify(dealershipsSelected.value))

const conversionsApiRequest = useApiRequest()
const conversionsData = ref([])
const filters = ref([])

const dateRange = ref({ start: dayjs().startOf('month').format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') })
const dateRangePresets = [
  datePresets.lastMonth,
  {
    label: 'Month-to-date',
    start: () => dayjs().startOf('month'),
    end: () => dayjs().endOf('day')
  },
  {
    label: 'Week-to-date',
    start: () => dayjs().startOf('week'),
    end: () => dayjs().endOf('day')
  }
]

function initFilters() {
  filters.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS }
  }
}
initFilters()

function clearFilters() {
  initFilters()
}

function fetchConversionsData() {
  // Page-level dealership filter is required before hitting the API
  if (!dealershipsSelected.value.length) {
    conversionsData.value = []
    return
  }

  try {
    conversionsApiRequest
      .send({
        endpoint: 'v1/reports/appointments/source-conversions',
        params: {
          start: dayjs(dateRange.value.start).format('YYYY-MM-DD'),
          end: dayjs(dateRange.value.end).format('YYYY-MM-DD'),
          dealerships: dealershipsSelected.value.join(',')
        }
      })
      .then(response => {
        if (response.success) {
          conversionsData.value = response.data
        } else {
          alertToast('Failed to fetch data', response.message, 'error')
        }
      })
  } catch (err) {
    alertToast('Failed to fetch data', err.message, 'error')
  }
}
fetchConversionsData()

function onUpdateData() {
  localStorage.setItem(dealershipsSelectedStorageKey, JSON.stringify(dealershipsSelected.value))

  // Require a complete range before refetching
  if (dateRange.value.start && dateRange.value.end) {
    fetchConversionsData()
  }
}

watch(dealershipsSelected, value => {
  localStorage.setItem(dealershipsSelectedStorageKey, JSON.stringify(value))
})

const exportCSV = () => {
  dt.value.exportCSV()
}
</script>

<style></style>
